import type { CoBuyer, IShopper, VehicleInsurance } from '@makemydeal/dr-platform-types';
import { mapVehicleInsuranceForSync, type SyncVehicleInsurance } from '@makemydeal/dr-shared-ui-utils';
import type { CustomerFormValues } from '../types/CustomerCardTypes';
import type { CustomerSpdValues } from '../types/customerSpd.types';
import type { Address } from '../components/sections/addresses/address.utils';

/** Mirrors the CustomerData shape expected by the BFF /api/sync-customer endpoint. */
export interface SyncCustomerData {
    commonConsumerId: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    title?: string;
    suffix?: string;
    email?: string;
    emailAlternative?: string;
    /** Cell phone — only set when the primary phone IS the cell phone; otherwise preserves the original cell. */
    cellPhone?: string;
    homePhone?: string;
    workPhone?: string;
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    county?: string;
    zip?: string;
    previousAddress?: Address;
    maritalStatus?: string;
    customerType?: string;
    business?: {
        legalName?: string;
        businessEntityId?: string;
    };
    /** Only the VehicleInsurance fields mapped to Common Consumer's individual.insurance.primary shape. */
    insurance?: SyncVehicleInsurance;
    /** Only the SPD fields the dealer changed, mapped to Common Consumer's individual.sensitiveData shape. */
    spd?: SyncCustomerSpd;
}

/** The SPD subset synced to Common Consumer — bankAccountNumber has no Common Consumer equivalent. */
export type SyncCustomerSpd = Omit<CustomerSpdValues, 'bankAccountNumber'>;

/**
 * Detects which typed phone field (cellPhone/workPhone/homePhone) the
 * IShopper/CoBuyer primary phone (.phone) represents by finding which
 * typed field holds the same value. Defaults to 'cellPhone' when no match.
 */
const determinePhoneType = (customer: IShopper | CoBuyer): 'cellPhone' | 'workPhone' | 'homePhone' => {
    if (!customer.phone) return 'cellPhone';
    const types = ['cellPhone', 'workPhone', 'homePhone'] as const;
    return types.find((t) => customer[t] === customer.phone) ?? 'cellPhone';
};

/**
 * Maps a CustomerFormValues snapshot into the SyncCustomerData shape expected
 * by the BFF syncCustomer handler.
 *
 * commonConsumerId is required — callers must ensure it is set on the values
 * before invoking this transform (useCustomerCardActions enriches it from the store).
 *
 * Pass originalCustomer so the primary phone can be correctly routed to cellPhone,
 * workPhone, or homePhone. Without it the mapper defaults to treating phone as cell.
 *
 * Pass insurance to fold in the subset of VehicleInsurance fields that are mapped to
 * Common Consumer (see getInsuranceInfoPatch in syncConsumerService) — only pass this
 * for the buyer, since insurance is deal-level and is not tied to the co-buyer.
 *
 * Pass spd to fold in this participant's changed SPD fields (see getSensitiveDataPatch in
 * syncConsumerService). Only the fields the dealer actually edited are present, so the
 * resulting Common Consumer patch stays sparse.
 */
export const toSyncCustomerData = (
    values: CustomerFormValues,
    originalCustomer?: IShopper | CoBuyer | null,
    insurance?: VehicleInsurance | null,
    spd?: SyncCustomerSpd | null,
    enableCorrectPhoneHandling?: boolean
): SyncCustomerData => {
    const {
        commonConsumerId,
        firstName,
        middleName,
        lastName,
        title,
        suffix,
        email,
        emailAlternative,
        phone,
        cellPhone,
        homePhone,
        workPhone,
        streetAddress,
        streetAddress2,
        city,
        state,
        county,
        zip,
        previousAddress,
        maritalStatus,
        customerType,
        business
    } = values;

    if (!commonConsumerId) {
        throw new Error('commonConsumerId is required for syncCustomer');
    }

    const phoneType = originalCustomer ? determinePhoneType(originalCustomer) : 'cellPhone';
    const originalCellPhone = originalCustomer?.cellPhone;

    // The form has only one phone input (primary). The BFF overwrites all typed phone fields
    // on every sync, so we must always send cellPhone — either the updated value (when
    // primary IS cell) or the original value (to prevent blanking it when primary is not cell).
    const cellPhoneValue = enableCorrectPhoneHandling
        ? cellPhone
        : phone !== undefined
        ? phoneType === 'cellPhone'
            ? phone
            : originalCellPhone
        : undefined;

    return {
        commonConsumerId,
        ...(firstName !== undefined && { firstName }),
        ...(middleName !== undefined && { middleName }),
        ...(lastName !== undefined && { lastName }),
        ...(title !== undefined && { title }),
        ...(suffix !== undefined && { suffix }),
        ...(email !== undefined && { email }),
        ...(emailAlternative !== undefined && { emailAlternative }),
        ...(cellPhoneValue !== undefined && { cellPhone: cellPhoneValue }),
        ...(homePhone !== undefined && { homePhone }),
        ...(workPhone !== undefined && { workPhone }),
        ...(streetAddress !== undefined && { streetAddress }),
        ...(streetAddress2 !== undefined && { streetAddress2 }),
        ...(city !== undefined && { city }),
        ...(state !== undefined && { state }),
        ...(county !== undefined && { county }),
        ...(zip !== undefined && { zip }),
        ...(previousAddress !== undefined && { previousAddress }),
        ...(maritalStatus !== undefined && { maritalStatus }),
        ...(customerType !== undefined && { customerType }),
        ...(business !== undefined && { business }),
        ...(insurance && { insurance: mapVehicleInsuranceForSync(insurance) }),
        ...(spd && { spd })
    };
};
